import { useEffect, useRef, useState } from 'react';
import { get, post, qs, ApiError } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { formatPhone, normalizePhone } from '../../shared/format';
import { formatCents } from '../../shared/money';
import { Button, Field, Money, Sheet, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import type { CartCustomer } from '../lib/cart';
import { usePlan } from '../lib/plan';

interface Hit { id: string; name: string; phone: string | null; email: string | null; store_credit_cents: number; points_balance?: number; member_code?: string | null }

/** What was typed in a search, as a new customer's name or phone. */
function newCustomerFrom(text: string) {
  const digits = text.replace(/\D/g, '');
  const looksLikePhone = digits.length >= 7 && digits.length === text.replace(/[\s()+-]/g, '').length;
  const [first, ...rest] = looksLikePhone ? [''] : text.trim().split(/\s+/);
  return { phone: looksLikePhone ? text.trim() : '', firstName: first ?? '', lastName: rest.join(' ') };
}

/** Find a customer by phone or name, or add a new one in a few taps. `startAdding` opens the new-customer form with what was typed. */
export function CustomerPicker({ onPick, onClose, startAdding }: { onPick: (c: CartCustomer) => void; onClose: () => void; startAdding?: string }) {
  const toast = useToast();
  const [q, setQ] = useState(startAdding ?? '');
  const dq = useDebounced(q, 200);
  const [hits, setHits] = useState<Hit[]>([]);
  const [adding, setAdding] = useState(startAdding !== undefined);
  const [f, setF] = useState(() => ({ firstName: '', lastName: '', phone: '', email: '', smsOptIn: true, marketingOptIn: false, ...(startAdding !== undefined ? newCustomerFrom(startAdding) : {}) }));
  const [busy, setBusy] = useState(false);
  // Texts and deals by text run on West Coast Wireless's own number: its store only, for now. Other
  // shops aren't asked, and nobody is marked as agreeing to texts nobody can send.
  const texting = usePlan().home;

  useEffect(() => {
    let live = true;
    get<Hit[]>(`/api/customers${qs({ q: dq, limit: 8 })}`).then((h) => live && setHits(h)).catch(() => undefined);
    return () => { live = false; };
  }, [dq]);

  const startAdd = () => {
    setF({ ...f, ...newCustomerFrom(q) });
    setAdding(true);
  };

  const create = async () => {
    setBusy(true);
    try {
      const c = await post('/api/customers', { ...f, smsOptIn: texting && f.smsOptIn, marketingOptIn: texting && f.marketingOptIn, email: f.email || null, phone: f.phone || null, emailOptIn: !!f.email });
      onPick({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict' && e.details?.customerId) {
        const c = await get(`/api/customers/${e.details.customerId}`);
        toast(`${c.name} already has that number, so we picked them.`);
        onPick({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
      } else {
        toast(e instanceof Error ? e.message : 'Could not add the customer.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  if (adding) {
    const phoneOk = !f.phone || !!normalizePhone(f.phone);
    return (
      <Sheet
        title="New customer"
        onClose={onClose}
        footer={<><Button onClick={() => (startAdding !== undefined ? onClose() : setAdding(false))}>Back</Button><Button variant="primary" busy={busy} disabled={!phoneOk || (!f.firstName && !f.phone)} onClick={create}>Add customer</Button></>}
      >
        <div className="form-grid">
          <Field label="Mobile number" className="full"><input className="input input-lg" inputMode="tel" autoFocus value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(510) 555-0123" /></Field>
          <Field label="First name"><input className="input" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></Field>
          <Field label="Last name"><input className="input" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></Field>
          <Field label="Email (optional)" className="full"><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          {texting ? (
            <>
              <label className="check full"><input type="checkbox" checked={f.smsOptIn} onChange={(e) => setF({ ...f, smsOptIn: e.target.checked })} /> Customer agrees to receive texts about their orders and repairs</label>
              <label className="check full"><input type="checkbox" checked={f.marketingOptIn} onChange={(e) => setF({ ...f, marketingOptIn: e.target.checked })} /> Customer wants deals and rewards news by text and email (ask them)</label>
            </>
          ) : null}
        </div>
        {!phoneOk ? <div className="error-text">Mobile numbers need 10 digits.</div> : null}
      </Sheet>
    );
  }

  return (
    <Sheet title="Customer" onClose={onClose} footer={<Button variant="primary" icon="plus" onClick={startAdd}>New customer</Button>}>
      <div className="search">
        <Icon.search />
        <input className="input input-lg" autoFocus placeholder="Phone number or name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search customers" />
      </div>
      <div className="list">
        {hits.length ? (
          hits.map((h) => (
            <button key={h.id} type="button" className="list-row" onClick={() => onPick(h)}>
              <span className="avatar avatar-sm">{h.name.slice(0, 1)}</span>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{h.name}</span>
                <span className="s" style={{ display: 'block' }}>{[formatPhone(h.phone), h.email].filter(Boolean).join(' · ') || 'No contact info'}</span>
              </span>
              {h.points_balance ? <span className="badge">{h.points_balance.toLocaleString('en-US')} pts</span> : null}
              {h.store_credit_cents ? <span className="badge badge-green">Credit <Money cents={h.store_credit_cents} /></span> : null}
            </button>
          ))
        ) : (
          <div className="empty">No customers match. Add them as a new customer.</div>
        )}
      </div>
    </Sheet>
  );
}

/**
 * "Search customer name": type a name or phone number and matching customers drop down; tap one
 * (or press Enter) to put them on this repair, buy, trade-in, order, unlock, sale or activation.
 * Their details come with them. Not on file? "Add new customer" right from the list.
 */
export function CustomerSearch({ value, onChange, placeholder = 'Search customer name or phone', autoFocus = false, removable = true }: {
  value: CartCustomer | null;
  onChange: (c: CartCustomer | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Show "Change" on the picked customer. */
  removable?: boolean;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 180);
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dq.length < 2) { setHits(null); return; }
    let live = true;
    get<Hit[]>(`/api/customers${qs({ q: dq, limit: 8 })}`).then((h) => { if (live) { setHits(h); setActive(0); } }).catch(() => live && setHits([]));
    return () => { live = false; };
  }, [dq]);

  const pick = (h: Hit) => {
    onChange({ id: h.id, name: h.name, phone: h.phone, email: h.email, store_credit_cents: h.store_credit_cents, points_balance: h.points_balance, member_code: h.member_code });
    setQ('');
    setHits(null);
    setOpen(false);
  };

  if (value) {
    return (
      <div className="customer-pill">
        <Icon.user />
        <div className="grow">
          <div className="strong">{value.name}</div>
          <div className="small muted">{[formatPhone(value.phone) || 'No phone', value.store_credit_cents ? `${formatCents(value.store_credit_cents)} store credit` : null].filter(Boolean).join(' · ')}</div>
        </div>
        {removable ? <Button size="sm" onClick={() => { onChange(null); setTimeout(() => input.current?.focus(), 0); }}>Change</Button> : null}
      </div>
    );
  }

  const list = hits ?? [];
  const showDrop = open && q.trim().length >= 2;
  return (
    <div className="cust-search">
      <div className="search">
        <Icon.search />
        <input
          ref={input}
          className="input input-lg"
          value={q}
          autoFocus={autoFocus}
          placeholder={placeholder}
          aria-label="Search customer name or phone"
          role="combobox"
          aria-expanded={showDrop}
          aria-autocomplete="list"
          autoComplete="off"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, list.length)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            else if (e.key === 'Enter' && showDrop) { e.preventDefault(); if (list[active]) pick(list[active]); else if (q.trim()) setAdding(q.trim()); }
            else if (e.key === 'Escape') setOpen(false);
          }}
        />
      </div>
      {showDrop ? (
        <div className="cust-drop" role="listbox" aria-label="Customers">
          {list.map((h, i) => (
            <button key={h.id} type="button" role="option" aria-selected={i === active} className="cust-hit" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(h)}>
              <span className="avatar avatar-sm">{h.name.slice(0, 1)}</span>
              <span className="grow">
                <span className="t">{h.name}</span>
                <span className="s">{[formatPhone(h.phone), h.email].filter(Boolean).join(' · ') || 'No contact info'}</span>
              </span>
              {h.store_credit_cents ? <span className="badge badge-green">Credit <Money cents={h.store_credit_cents} /></span> : null}
            </button>
          ))}
          {hits && !list.length ? <div className="cust-none small muted">No customer matches “{q.trim()}”.</div> : null}
          {!hits ? <div className="cust-none small muted">Searching…</div> : null}
          <button type="button" role="option" aria-selected={active === list.length} className="cust-hit cust-add" onMouseDown={(e) => e.preventDefault()} onClick={() => setAdding(q.trim())}>
            <span className="avatar avatar-sm"><Icon.plus /></span>
            <span className="grow"><span className="t">Add new customer</span><span className="s">“{q.trim()}”</span></span>
          </button>
        </div>
      ) : null}
      {adding !== null ? <CustomerPicker startAdding={adding} onClose={() => setAdding(null)} onPick={(c) => { onChange(c); setAdding(null); setQ(''); setOpen(false); }} /> : null}
    </div>
  );
}
